import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, signOut, fetchUserAttributes, updateUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import logger from '../services/logger';
import { v4 as uuidv4 } from 'uuid';
import configService from '../services/configService';

const AuthContext = createContext();

const storeRefreshToken = async (cognitoUserId, refreshToken, lastAuthUser, userType, groups) => {
  try {
    // Only store refresh tokens for approved users (not waitlist users)
    // Check groups first, then fallback to user_type for backward compatibility
    const isWaitlistUser = groups.includes('waitlist') || userType === 'waitlist';
    
    if (isWaitlistUser) {
      console.log('Skipping refresh token storage for waitlist user');
      return;
    }
    
    // Get ID token directly from localStorage to avoid circular dependency
    const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
    const idToken = localStorage.getItem(idTokenKey);
    
    if (!idToken) {
      console.warn('No ID token available for storing refresh token');
      return;
    }
    
    await fetch('https://oauth.remote-assistant.ai/oauth/secure-auth', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cognito_user_id: cognitoUserId,
        refresh_token: refreshToken
      })
    });
  } catch (error) {
    console.warn('Failed to store refresh token:', error);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Listen for authentication events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      console.log('Auth event:', event);
      if (event === 'signedIn' || event === 'autoSignIn') {
        checkAuthState();
      } else if (event === 'signedOut') {
        setUser(null);
        window.user = null;
        window.authToken = null;
      }
    });

    // Initial check of the auth state
    checkAuthState();

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const checkAuthState = async () => {
    try {
      // Check localStorage first for faster recovery
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      if (!lastAuthUser) {
        setUser(null);
        window.user = null;
        window.authToken = null;
        return;
      }
      
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      if (!idToken) {
        setUser(null);
        window.user = null;
        window.authToken = null;
        return;
      }
      
      // Check if token is expired
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        setUser(null);
        window.user = null;
        window.authToken = null;
        return;
      }

      // Check if user is in waitlist group and auto-logout
      const groups = payload['cognito:groups'] || [];
      if (groups.includes('waitlist')) {
        // Check if user was recently approved by looking for approval timestamp
        const approvalCheck = localStorage.getItem('user_approval_check');
        const now = Date.now();
        
        // If we haven't checked recently, verify current group status
        if (!approvalCheck || (now - parseInt(approvalCheck)) > 60000) { // Check every minute
          localStorage.setItem('user_approval_check', now.toString());
          
          // Try to get fresh user attributes to check if still in waitlist
          try {
            const freshAttributes = await fetchUserAttributes();
            const freshGroups = freshAttributes['cognito:groups']?.split(',') || [];
            
            // If user is no longer in waitlist group, force token refresh
            if (!freshGroups.includes('waitlist')) {
              await signOut();
              // Don't set waitlist logout message since this is an approval
              return;
            }
          } catch (e) {
            // Could not verify group status, proceed with logout
          }
        }
        
        await signOut();
        setUser(null);
        window.user = null;
        window.authToken = null;
        // Set a flag to show waitlist message on login page
        localStorage.setItem('waitlist_logout_message', 'true');
        return;
      }

      // Create user object from localStorage immediately
      const userWithAttributes = {
        username: lastAuthUser,
        userId: payload.sub,
        signInDetails: { loginId: payload.email },
        attributes: {
          sub: payload.sub,
          email: payload.email,
          given_name: payload.given_name,
          family_name: payload.family_name,
          'custom:business_id': payload['custom:business_id'],
          'custom:setup_completed': payload['custom:setup_completed'],
          'custom:subscription_status': payload['custom:subscription_status'],
          phone_number: payload.phone_number,
          email_verified: payload.email_verified,
          phone_number_verified: payload.phone_number_verified,
          'custom:beta_tester': payload['custom:beta_tester'] || 'true'
        }
      };
      
      // Check if user is admin
      const adminStatus = groups.includes('admin') || payload['custom:admin'] === 'true';
      setIsAdmin(adminStatus);
      
      setUser(userWithAttributes);
      window.user = userWithAttributes;
      window.authToken = idToken;
      window.isAdmin = adminStatus;
      
      // Notify cache of user change
      window.dispatchEvent(new CustomEvent('authUserChanged', { 
        detail: { userId: userWithAttributes.userId } 
      }));
      
      // Try AWS Amplify in background (non-blocking)
      Promise.all([
        getCurrentUser().catch(() => null),
        fetchUserAttributes().catch(() => null)
      ]).then(([currentUser, fetchedAttributes]) => {
        if (currentUser && fetchedAttributes) {
          const updatedUser = { ...currentUser, attributes: fetchedAttributes };
          setUser(updatedUser);
          window.user = updatedUser;
        }
      });
      
      // Log frontend auth event and store refresh token in background (non-blocking)
      Promise.all([
        logger.logFrontendAuth(userWithAttributes).catch(e => console.warn('Failed to log auth:', e)),
        (async () => {
          const refreshTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.refreshToken`;
          const refreshToken = localStorage.getItem(refreshTokenKey);
          if (refreshToken && userWithAttributes?.userId) {
            const userType = payload['custom:user_type'];
            await storeRefreshToken(userWithAttributes.userId, refreshToken, lastAuthUser, userType, groups);
          }
        })().catch(e => console.warn('Failed to store refresh token:', e))
      ]);
    } catch (error) {
      setUser(null);
      window.user = null;
      window.authToken = null;
    } finally {
      setLoading(false);
      window.checkAuthState = checkAuthState;
      window.loading = false;
      
      // Debug function for signup flow issues
      window.debugSignupFlow = () => {
        console.log('=== SIGNUP FLOW DEBUG ===');
        console.log('Current user:', window.user);
        console.log('Current URL:', window.location.href);
        console.log('LocalStorage tokens:');
        const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
        console.log('Last auth user:', lastAuthUser);
        if (lastAuthUser) {
          const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
          const idToken = localStorage.getItem(idTokenKey);
          if (idToken) {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            console.log('JWT payload:', payload);
            console.log('Token expired?', payload.exp * 1000 < Date.now());
          }
        }
      };
    }
  };

  const logout = async () => {
    try {
      if (user) {
        await logger.logUserLogout(user);
      }
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      // Clear only Cognito-related localStorage keys (no token revocation)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider') || 
            key.includes('amplify') ||
            key === 'waitlist_logout_message' ||
            key === 'user_approval_check') {
          localStorage.removeItem(key);
        }
      });
      
      // Dispatch event to clear data cache
      window.dispatchEvent(new CustomEvent('clearDataCache'));
      
      // Clear the user state in the app
      setUser(null);
      window.user = null;
      window.authToken = null;
    }
  };

  const updateUserProfile = async (attributes) => {
    try {
      if (!attributes || typeof attributes !== 'object') {
        throw new Error('Invalid attributes provided');
      }
      
      // Filter out undefined, null, and empty string values
      const cleanAttributes = Object.fromEntries(
        Object.entries(attributes).filter(([key, value]) => 
          value !== undefined && value !== null && value !== ''
        )
      );
      

      if (Object.keys(cleanAttributes).length === 0) {
        return;
      }

      try {
        // AWS Amplify v6 expects userAttributes in a specific format
        await updateUserAttributes({ userAttributes: cleanAttributes });
      } catch (amplifyError) {
        throw amplifyError;
      }
      
      // Force token refresh and retry until attributes are updated in JWT
      let attempts = 0;
      const maxAttempts = 10;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, attempts * 300)); // Exponential backoff
        
        // Force refresh the auth session to get new token with updated attributes
        await fetchAuthSession({ forceRefresh: true });
        
        // Read JWT directly from localStorage to check custom attributes
        const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
        if (!lastAuthUser) {
          throw new Error('No authenticated user found');
        }
        
        const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
        const idToken = localStorage.getItem(idTokenKey);
        
        if (!idToken) {
          throw new Error('No ID token found');
        }
        
        // Decode JWT to check custom attributes
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        
        // Check if all requested attributes are now present with correct values in JWT
        const allMatch = Object.entries(cleanAttributes).every(([key, value]) => 
          payload[key] === value
        );
        
        console.log(`🔄 JWT UPDATE: Attempt ${attempts + 1}/${maxAttempts}, attributes match:`, allMatch, {
          requested: cleanAttributes,
          jwt_has: Object.fromEntries(Object.keys(cleanAttributes).map(k => [k, payload[k]]))
        });
        
        if (allMatch) {
          // Update user state with fresh JWT data
          const [currentUser, freshAttributes] = await Promise.all([
            getCurrentUser(),
            fetchUserAttributes()
          ]);
          
          const updatedUser = { 
            ...currentUser, 
            attributes: {
              ...freshAttributes,
              ...Object.fromEntries(
                Object.keys(cleanAttributes)
                  .filter(k => k.startsWith('custom:'))
                  .map(k => [k, payload[k]])
              )
            }
          };
          
          setUser(updatedUser);
          window.user = updatedUser;
          window.authToken = idToken;
          
          console.log('✅ JWT UPDATE: Successfully updated and verified attributes');
          break;
        }
        
        attempts++;
      }
      
      if (attempts === maxAttempts) {
        console.error('❌ JWT UPDATE: Failed to verify attribute update after', maxAttempts, 'attempts');
        throw new Error('Failed to verify JWT attribute update');
      }
    } catch (error) {
      console.error('Error updating user attributes:', error);
      throw error;
    }
  };



  const value = {
    user,
    loading,
    isAdmin,
    logout,
    updateUserProfile,
    checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};